/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css } from 'lit-element';

export const inputTextStyles = css`
  :host {
    width: 100%;
    height: auto;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .input-text-label {
    color: var(--text-secondary);
    position: absolute;
    left: 0.5rem;
    width: calc(100% - 1rem);
    transition: var(--transition-time) all ease-in-out;
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    top: 0.5rem;
    font: var(--body-1);
    line-height: 1.5rem;
    display: flex;
  }
  :host(:not(.empty)) .input-text-label,
  :host(:not([readonly])) input:focus + .input-text-label,
  :host(:not([readonly])) .textarea:focus + .input-text-label,
  :host([textarea]:not(.empty)) .input-text-label,
  :host(:not([readonly])[show-outline]) .input-text-label {
    top: 0.25rem;
    font: var(--body-2);
    line-height: 1rem;
  }
  .label-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: fit-content;
  }
  input {
    outline: none;
    border: none;
    font: var(--body-1);
    color: var(--text-primary);
    background: rgba(var(--ui-1), var(--opacity-7));
    height: 2.5rem;
    border-radius: 0.125rem 0.125rem 0rem 0rem;
    width: 100%;
    box-sizing: border-box;
    text-overflow: ellipsis;
    transition: var(--transition-time) all ease-in-out;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    -webkit-appearance: none;
    border-bottom: 0.0625rem solid transparent;
    border-color: rgba(var(--ui-1), var(--opacity-4));
    padding: 1.25rem 0.5rem 0.25rem 0.5rem;
  }
  input:disabled,
  textarea:disabled {
    opacity: unset;
  }
  /* focus */
  input:focus:not([error]),
  input:focus:not([success]),
  input:focus:not([safety]),
  :host([show-outline]) input {
    border-color: rgba(var(--ui-1), var(--opacity-2));
  }
  :host([readonly]) input:focus {
    border-color: rgba(var(--ui-1), var(--opacity-4));
  }
  input::-ms-clear,
  input::-ms-reveal {
    display: none;
  }

  :host([readonly]) input:focus + .textarea + .input-text-label,
  :host([readonly]) .textarea:focus + .input-text-label {
    color: var(--text-secondary);
  }
  :host(.empty) input {
    padding: 0.5rem;
  }

  :host(.empty:not([condensed])) input:focus,
  .textarea:focus {
    padding: 1.25rem 0.5rem 0.25rem 0.5rem;
  }

  :host([readonly]) input,
  :host([readonly]) .textarea,
  :host([disabled]) input,
  :host([disabled]) .textarea {
    cursor: default;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  :host([mandatory][disabled]) #mandatory {
    color: rgba(var(--ui-2), var(--opacity-5));
  }
  :host([readonly]) input,
  :host([readonly]) .textarea {
    background: transparent;
  }
  .content-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
  }
  :host([disabled]) .icon-wrapper > ::slotted(*) {
    pointer-events: none;
    color: var(--text-disabled);
  }
  :host([disabled][textarea]) .textarea {
    color: var(--text-disabled);
  }
  .icon-wrapper {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    gap: 0.5rem;
  }
  .icon-wrapper > ::slotted(sh-icon) {
    font-size: 1.5rem;
    line-height: 1.5rem;
    min-height: 1.5rem;
    min-width: 1.5rem;
  }
  /*condensed*/
  :host(:not([textarea])[condensed]) .input-text-label,
  :host([condensed]) input:focus + .input-text-label {
    display: none;
  }

  :host(.empty[condensed][readonly]) input:focus + .input-text-label {
    display: flex;
  }
  :host(.empty[condensed]) .input-text-label {
    display: flex;
    top: 0.25rem;
  }
  :host([condensed]) input,
  :host([condensed][no-clear]) input {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    height: 2rem;
  }
  :host([disabled]) .input-text-label,
  :host([disabled][textarea]) .input-text-label,
  :host([disabled]) input,
  :host([disabled]) .textarea {
    color: var(--text-disabled);
  }
  :host([condensed]) input:hover .icon-wrapper > ::slotted(sh-icon) {
    font-size: 1.5rem;
    line-height: 1.5rem;
    min-height: 1.5rem;
    min-width: 1.5rem;
  }
  :host([condensed]) .icon-wrapper,
  :host([_iconslot][condensed][error]) .icon-wrapper,
  :host([_iconslot][condensed][success]) .icon-wrapper,
  :host([textarea]) .icon-wrapper {
    top: 0.25rem;
  }
  /* Validation Styles */
  :host([mandatory]) #mandatory {
    display: inline-block;
    color: rgba(var(--ui-2), var(--opacity-1));
  }
  :host([error]) input,
  :host([error]) input:focus,
  :host([textarea][error]) .content-wrapper {
    border-color: rgba(var(--functional-red), var(--opacity-1));
  }
  :host([success]) input,
  :host([success]) input:focus,
  :host([textarea][success]) .content-wrapper {
    border-color: rgba(var(--functional-green), var(--opacity-1));
  }
  :host([safety]) input,
  :host([safety]) input:focus,
  :host([textarea][safety]) .content-wrapper {
    border-color: rgba(var(--functional-yellow), var(--opacity-2));
  }
  /* Clear Field Icon */
  #clear {
    opacity: 0;
    z-index: -1;
    pointer-events: none;
    transition: none;
    cursor: pointer;
    max-width: 0;
    max-height: 0;
    min-width: 0;
    min-height: 0;
  }
  :host([readonly]) #clear,
  :host([disabled]) #clear {
    opacity: 0;
    z-index: -1;
    max-width: 0;
    max-height: 0;
    min-width: 0;
    min-height: 0;
  }
  :host([error]:not([value=''])) #clear {
    opacity: 1;
    z-index: auto;
    min-width: 1.5rem;
    min-height: 1.5rem;
    max-height: 1.5rem;
    max-width: 1.5rem;
  }
  /* No Clear */
  :host([no-clear]) input,
  :host(:hover:not(.empty):not([condensed]):not([readonly]):not([disabled])[no-clear]) input {
    padding: 1rem 0.5rem 0 0.5rem;
  }
  /* hover */
  :host(:not([readonly]):not([disabled]):hover) input {
    background: rgba(var(--ui-1), var(--opacity-6));
  }
  :host(:not([readonly]):not([disabled]):not(.empty):hover:not(.no-hovermq)) #clear,
  :host(:not([readonly]):not([disabled]):not(.empty).no-hovermq):hover #clear,
  #clear:focus {
    pointer-events: all;
    opacity: 1;
    z-index: auto;
    min-width: 1.5rem;
    min-height: 1.5rem;
    max-height: 1.5rem;
    max-width: 1.5rem;
  }

  :host([textarea]) .input-text-label {
    background-color: transparent;
    transition: color var(--transition-time) ease-in-out, font var(--transition-time) ease-in-out,
      top var(--transition-time) ease-in-out, line-height var(--transition-time) ease-in-out;
    top: 0.25rem;
  }

  :host([textarea]) .textarea,
  :host([readonly][textarea]) .textarea:focus {
    outline: none;
    border: none;
    font: var(--body-1);
    color: var(--text-primary);
    background-color: transparent;
    width: 100%;
    box-sizing: border-box;
    padding: 0rem;
    resize: none;
    overflow-y: auto;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
  }
  :host([textarea]) .content-wrapper {
    transition: var(--transition-time) all ease-in-out, 0s height;
    border-radius: 0.125rem 0.125rem 0rem 0rem;
    border-bottom: 0.0625rem solid transparent;
    border-color: rgba(var(--ui-1), var(--opacity-5));
    background: rgba(var(--ui-1), var(--opacity-7));
  }
  :host([textarea]:hover) .content-wrapper {
    background: rgba(var(--ui-1), var(--opacity-6));
  }
  :host([textarea][disabled]) .content-wrapper {
    background: rgba(var(--ui-1), var(--opacity-7));
  }
  :host(:not([readonly]):not(.no-hovermq)[textarea]:hover) input:hover:not(:focus),
  :host(:not([readonly]):not(.no-hovermq)[textarea]:hover) .textarea:hover:not(:focus),
  :host([textarea][readonly]),
  :host([textarea][readonly]) .content-wrapper {
    background-color: transparent;
  }
  :host([textarea]) #shinputDiv {
    padding: 2rem 0.5rem 0.5rem 0.5rem;
    box-sizing: border-box;
  }
  :host([textarea]:not([readonly]):not([disabled])) #shinputDiv {
    cursor: text;
  }
  :host([value=''][readonly]) {
    pointer-events: none;
  }
  :host([disabled]:not([readonly])) input,
  :host([disabled][textarea]) textarea :host([disabled]:hover) input {
    background-color: rgba(var(--ui-1), var(--opacity-7));
  }
  :host(:hover:not(.empty):not([readonly]):not([disabled])[_iconslot][condensed][no-clear]) input {
    padding: 0.5rem 2.5rem 0.5rem 0.5rem;
  }
  :host(:hover[_iconslot]:not([readonly]):not([disabled]):not([condensed])) input {
    padding: 1rem 4.5rem 0rem 0.5rem;
  }

  /* helper text */
  .helper-text {
    padding: 0.25rem 0.5rem 0rem 0.5rem;
    box-sizing: border-box;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  :host([disabled]) .helper-text {
    color: var(--text-disabled);
  }
  /* textarea auto height */
  :host([textarea][rows='auto']) .content-wrapper,
  :host([textarea][rows='auto']) textarea {
    display: flex;
    flex: 1;
  }
  :host([no-border]) input,
  :host([no-border][readonly]) input:focus,
  :host([textarea][no-border]) .content-wrapper {
    border-bottom: none;
  }
  :host([_iconslot][error]) .icon-wrapper,
  :host([_iconslot][success]) .icon-wrapper {
    top: 0.5rem;
  }
  :host(.no-hovermq) #clear {
    opacity: 1;
    z-index: auto;
    pointer-events: all;
    min-width: 1.5rem;
    min-height: 1.5rem;
    max-height: 1.5rem;
    max-width: 1.5rem;
  }
  :host .textarea {
    cursor: auto;
  }
`;
